import React, { useEffect, useRef, useState } from "react";

interface LazyVideoProps {
    src: string;
    poster: string;
    className?: string;
    loop?: boolean;
    muted?: boolean;
    playsInline?: boolean;
}

export default function LazyVideo({
    src,
    className,
    loop = true,
    muted = true,
    playsInline = true,
    poster
}: LazyVideoProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const videoRef = useRef<HTMLVideoElement>(null);

    const [isVisible, setIsVisible] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);

    // Observe visibility
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            {
                root: null,
                threshold: 0.4 // 40% visible = good UX
            }
        );

        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    // Play / pause logic
    useEffect(() => {
        if (!videoRef.current) return;

        if (isVisible) {
            videoRef.current.play().catch(() => {});
        } else {
            videoRef.current.pause();
        }
    }, [isVisible]);

    return (
        <div ref={containerRef} className={className}>
            {isVisible && (
                <video
                    ref={videoRef}
                    src={src}
                    loop={loop}
                    muted={muted}
                    playsInline={playsInline}
                    preload="metadata"
                    onLoadedData={() => setHasLoaded(true)}
                    className="w-full h-full object-cover rounded-xl"
                />
            )}

            {/* Optional skeleton */}
            {!isVisible && (
                <img
                    src={poster}
                    className="w-full h-full object-cover scale-105 transition-opacity"
                    alt="Loading video..."
                />
            )}
        </div>
    );
}
